import { useEffect, useState } from 'react';
import { Td, Tr, Flex, Input, Select, Button, IconButton } from '@chakra-ui/react';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { DaysOfWeek, ScheduleWeek } from '@/model/schedule-week';
import { NewScheduleWeek } from './ApplicationCalendarWeekScheduleTable';
import { useTranslation } from 'react-i18next';

export interface ApplicationCalendarWeekScheduleTableRowProps {
  index: number;
  items: ScheduleWeek[];
  onSaveClick?: (newScheduleWeek: NewScheduleWeek) => void;
  onDeleteClick?: (newItemIndex: number) => void;
}

export const ApplicationCalendarWeekScheduleNewItemTableRow = ({
  index,
  items,
  onSaveClick,
  onDeleteClick,
}: ApplicationCalendarWeekScheduleTableRowProps) => {
  const [dayOfWeek, setDayOfWeek] = useState<DaysOfWeek>();
  const [description, setDescription] = useState('');
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<DaysOfWeek[]>();
  const { t } = useTranslation(); // Adicione seu hook de tradução

  useEffect(() => {
    const selectedDays = items.map(item => item.days_of_week); // Obtém os dias já usados
    setSelectedDaysOfWeek(selectedDays);

    const firstAvailableDay = Object.values(DaysOfWeek).find(day => !selectedDays.includes(day));

    if (firstAvailableDay) {
      setDayOfWeek(firstAvailableDay);
    }
  }, [items]); // ✅ Atualiza sempre que "items" m

  return (
    <Tr>
      <Td w={{ md: '20%' }}>
        <Select
          value={dayOfWeek}
          onChange={event => setDayOfWeek(event.target.value as DaysOfWeek)}
          backgroundColor='white'
          borderColor='gray.300'
          borderRadius={8}
          focusBorderColor='brand.300'
          css={{ '& option[disabled]': { color: 'var(--qgx-theme-colors-gray-300)' } }}
        >
          <option value='' disabled>
            {t('pages.application_calendar_week.components.new_item_table_row.select_placeholder')}
          </option>
          <option value={DaysOfWeek.SUNDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.SUNDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.sunday')}
          </option>
          <option value={DaysOfWeek.MONDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.MONDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.monday')}
          </option>
          <option value={DaysOfWeek.TUESDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.TUESDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.tuesday')}
          </option>
          <option value={DaysOfWeek.WEDNESDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.WEDNESDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.wednesday')}
          </option>
          <option value={DaysOfWeek.THURSDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.THURSDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.thursday')}
          </option>
          <option value={DaysOfWeek.FRIDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.FRIDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.friday')}
          </option>
          <option value={DaysOfWeek.SATURDAY} disabled={selectedDaysOfWeek?.includes(DaysOfWeek.SATURDAY)}>
            {t('pages.application_calendar_week.components.new_item_table_row.saturday')}
          </option>
        </Select>
      </Td>

      <Td>
        <Input
          maxW={{ md: '80%' }}
          textAlign='left'
          value={description}
          onChange={event => setDescription(event.target.value)}
          backgroundColor='white'
          borderColor='gray.300'
          borderRadius={8}
        />
      </Td>

      <Td
        w={{ md: '10%' }}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <Flex gap={4} alignItems='center'>
          <Button
            onClick={() => {
              onSaveClick?.({
                days_of_week: dayOfWeek as DaysOfWeek,
                description,
              });
            }}
          >
            {t('pages.application_calendar_week.components.new_item_table_row.save_button')}
          </Button>

          <IconButton
            aria-label={t('pages.application_calendar_week.components.new_item_table_row.delete_aria_label')}
            variant=''
            icon={<ChakraFeatherIcon name='trash-2' color='red.600' onClick={() => onDeleteClick?.(index)} />}
          />
        </Flex>
      </Td>
    </Tr>
  );
};
